// user/login/UserLogin.js
import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Button,
  Container,
  Divider,
  Stack,
  TextField,
  Typography,
  IconButton,
  InputAdornment,
  Paper,
  useTheme,
  alpha,
  Fade,
  MenuItem,
  ListItemText,
  InputBase,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { TranslateContext } from "../../context/TranslateContext";
import { GlobalContext } from "../../context/GlobalContext";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import EmailOutlined from "@mui/icons-material/EmailOutlined";
import LockOutlined from "@mui/icons-material/LockOutlined";
import PersonOutlined from "@mui/icons-material/PersonOutlined";
import PhoneOutlined from "@mui/icons-material/PhoneOutlined";
import LockResetOutlined from "@mui/icons-material/LockResetOutlined";
import MarkEmailRead from "@mui/icons-material/MarkEmailRead";
import CheckCircle from "@mui/icons-material/CheckCircle";
import MarkEmailReadIcon from "@mui/icons-material/MarkEmailRead";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { useHistory } from "react-router-dom";
import CommonDialog from "../../common/CommonDialog";
import ThemeToggle from "../../common/ThemeToggle";
import countryCode from "./countryCode";

// ─────────────────────────────────────────────────────────────────────────────
// Country Code Picker
// ─────────────────────────────────────────────────────────────────────────────
const CountryCodePicker = ({ value, onChange }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);

  const filtered = countryCode.filter(
    (c) =>
      c.name.toLowerCase().includes(search.toLowerCase()) ||
      c.dial_code.includes(search),
  );
  const selected =
    countryCode.find((c) => c.dial_code === value) || countryCode[0];

  return (
    <Box sx={{ position: "relative" }}>
      <Box
        onClick={() => setOpen((p) => !p)}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          px: 1,
          py: 0.5,
          borderRadius: 1.5,
          cursor: "pointer",
          userSelect: "none",
          bgcolor: alpha(theme.palette.text.primary, isDark ? 0.06 : 0.04),
          "&:hover": {
            bgcolor: alpha(theme.palette.text.primary, isDark ? 0.1 : 0.07),
          },
          minWidth: 68,
          justifyContent: "center",
          transition: "background 0.15s",
        }}
      >
        <Typography fontSize="1rem" lineHeight={1}>
          {selected.code
            .toUpperCase()
            .replace(/./g, (c) =>
              String.fromCodePoint(127397 + c.charCodeAt(0)),
            )}
        </Typography>
        <Typography
          variant="caption"
          fontWeight={600}
          sx={{ color: "text.primary", fontSize: "0.78rem" }}
        >
          {selected.dial_code}
        </Typography>
      </Box>

      {open && (
        <Paper
          elevation={8}
          sx={{
            position: "absolute",
            top: "calc(100% + 6px)",
            left: 0,
            zIndex: 1400,
            width: 260,
            maxHeight: 280,
            overflow: "hidden",
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            bgcolor: "background.paper",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              px: 1.5,
              py: 1,
              borderBottom: `1px solid ${theme.palette.divider}`,
            }}
          >
            <InputBase
              autoFocus
              fullWidth
              placeholder="Search country…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ fontSize: "0.82rem", color: "text.primary" }}
            />
          </Box>
          <Box sx={{ overflowY: "auto", flex: 1 }}>
            {filtered.length === 0 ? (
              <Typography
                variant="caption"
                sx={{ p: 2, color: "text.disabled", display: "block" }}
              >
                No results
              </Typography>
            ) : (
              filtered.map((c) => (
                <MenuItem
                  key={c.code}
                  selected={c.dial_code === value}
                  onClick={() => {
                    onChange(c.dial_code);
                    setOpen(false);
                    setSearch("");
                  }}
                  sx={{
                    py: 0.8,
                    px: 1.5,
                    gap: 1,
                    fontSize: "0.82rem",
                    "&.Mui-selected": {
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                    },
                  }}
                >
                  <Typography fontSize="1rem" lineHeight={1}>
                    {c.code
                      .toUpperCase()
                      .replace(/./g, (ch) =>
                        String.fromCodePoint(127397 + ch.charCodeAt(0)),
                      )}
                  </Typography>
                  <ListItemText
                    primary={c.name}
                    secondary={c.dial_code}
                    primaryTypographyProps={{
                      fontSize: "0.8rem",
                      noWrap: true,
                    }}
                    secondaryTypographyProps={{ fontSize: "0.72rem" }}
                  />
                </MenuItem>
              ))
            )}
          </Box>
        </Paper>
      )}
      {open && (
        <Box
          sx={{ position: "fixed", inset: 0, zIndex: 1399 }}
          onClick={() => {
            setOpen(false);
            setSearch("");
          }}
        />
      )}
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// ForgotPasswordDialog
// ─────────────────────────────────────────────────────────────────────────────
const ForgotPasswordDialog = ({ open, onClose, lang, hitAxios }) => {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);

  function handleClose() {
    onClose();
    setTimeout(() => {
      setEmail("");
      setSent(false);
    }, 300);
  }

  async function handleSubmit() {
    if (!email.trim()) return;
    const res = await hitAxios({
      path: "/api/user/forgot-password",
      post: true,
      obj: { email: email.trim() },
      showSnackbar: true,
    });
    if (res?.data?.success) setSent(true);
  }

  return (
    <CommonDialog
      open={open}
      onClose={handleClose}
      title={lang?.forgotPassword || "Forgot Password"}
      subtitle={
        sent
          ? lang?.checkYourEmail || "Check your email"
          : lang?.forgotPasswordSubtitle ||
            "Enter your email to receive a reset link"
      }
      icon={LockResetOutlined}
      maxWidth="xs"
    >
      {sent ? (
        <Stack spacing={3} alignItems="center" py={2}>
          <Box
            sx={{
              width: 72,
              height: 72,
              borderRadius: "50%",
              bgcolor: alpha("#10B981", 0.12),
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <MarkEmailRead sx={{ fontSize: 36, color: "success.main" }} />
          </Box>
          <Box textAlign="center">
            <Typography variant="body1" fontWeight={700} gutterBottom>
              {lang?.resetLinkSentTitle || "Reset link sent!"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {lang?.resetLinkSent ||
                "We've sent a password reset link to your email. It expires in 1 hour."}
            </Typography>
          </Box>
          <Stack spacing={1.5} width="100%">
            <Button
              fullWidth
              variant="outlined"
              onClick={handleSubmit}
              sx={{ fontWeight: 600, borderRadius: 2 }}
            >
              {lang?.resendResetLink || "Resend Reset Link"}
            </Button>
            <Button
              fullWidth
              variant="contained"
              onClick={handleClose}
              sx={{ fontWeight: 600, borderRadius: 2 }}
            >
              {lang?.close || "Close"}
            </Button>
          </Stack>
        </Stack>
      ) : (
        <Stack spacing={2.5}>
          <TextField
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            fullWidth
            placeholder={lang?.emailPlaceholder || "Email address"}
            type="email"
            label={lang?.email || "Email"}
            onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailOutlined
                    sx={{ color: "text.disabled", fontSize: 18 }}
                  />
                </InputAdornment>
              ),
            }}
          />
          <Button
            onClick={handleSubmit}
            fullWidth
            variant="contained"
            size="large"
            disabled={!email.trim()}
            sx={{ py: 1.4, fontWeight: 600, borderRadius: 2 }}
          >
            {lang?.sendResetLink || "Send Reset Link"}
          </Button>
        </Stack>
      )}
    </CommonDialog>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// ResetPasswordDialog
// ─────────────────────────────────────────────────────────────────────────────
const ResetPasswordDialog = ({
  open,
  onClose,
  token,
  lang,
  hitAxios,
  onSuccess,
}) => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [done, setDone] = useState(false);

  function handleClose() {
    onClose();
    setTimeout(() => {
      setPassword("");
      setConfirmPassword("");
      setShowPassword(false);
      setShowConfirm(false);
      setDone(false);
    }, 300);
  }

  async function handleReset() {
    if (!password || password !== confirmPassword) return;
    const res = await hitAxios({
      path: "/api/user/reset-password",
      post: true,
      obj: { token, password },
      showSnackbar: true,
    });
    if (res?.data?.success) setDone(true);
  }

  return (
    <CommonDialog
      open={open}
      onClose={handleClose}
      title={lang?.resetPassword || "Reset Password"}
      subtitle={
        done
          ? lang?.passwordResetDoneSubtitle || "Your password has been updated"
          : lang?.resetPasswordSubtitle || "Enter your new password below"
      }
      icon={LockResetOutlined}
      maxWidth="xs"
    >
      {done ? (
        <Stack spacing={3} alignItems="center" py={2}>
          <Box
            sx={{
              width: 72,
              height: 72,
              borderRadius: "50%",
              bgcolor: alpha("#10B981", 0.12),
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CheckCircle sx={{ fontSize: 36, color: "success.main" }} />
          </Box>
          <Box textAlign="center">
            <Typography variant="body1" fontWeight={700} gutterBottom>
              {lang?.passwordResetDone || "Password reset successfully!"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {lang?.passwordResetDoneMessage ||
                "You can now log in with your new password."}
            </Typography>
          </Box>
          <Button
            fullWidth
            variant="contained"
            onClick={() => {
              handleClose();
              onSuccess();
            }}
            sx={{ fontWeight: 600, borderRadius: 2 }}
          >
            {lang?.goToLogin || "Go to Login"}
          </Button>
        </Stack>
      ) : (
        <Stack spacing={2.5}>
          <TextField
            fullWidth
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            label={lang?.newPassword || "New Password"}
            type={showPassword ? "text" : "password"}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockOutlined sx={{ color: "text.disabled", fontSize: 18 }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    size="small"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            fullWidth
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            label={lang?.confirmPassword || "Confirm Password"}
            type={showConfirm ? "text" : "password"}
            error={confirmPassword.length > 0 && password !== confirmPassword}
            helperText={
              confirmPassword.length > 0 && password !== confirmPassword
                ? lang?.passwordMismatch || "Passwords do not match"
                : ""
            }
            onKeyDown={(e) => e.key === "Enter" && handleReset()}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockOutlined sx={{ color: "text.disabled", fontSize: 18 }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowConfirm(!showConfirm)}
                    edge="end"
                    size="small"
                  >
                    {showConfirm ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Button
            onClick={handleReset}
            fullWidth
            variant="contained"
            size="large"
            disabled={
              !password || !confirmPassword || password !== confirmPassword
            }
            sx={{ py: 1.4, fontWeight: 600, borderRadius: 2 }}
          >
            {lang?.resetPasswordBtn || "Reset Password"}
          </Button>
        </Stack>
      )}
    </CommonDialog>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Main UserLogin
// ─────────────────────────────────────────────────────────────────────────────
const UserLogin = () => {
  const history = useHistory();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const { lang } = useContext(TranslateContext);
  const { hitAxios, webData } = useContext(GlobalContext);

  const [mode, setMode] = useState("login");
  const [pendingEmail, setPendingEmail] = useState("");
  const [otpValue, setOtpValue] = useState("");
  const [otpLoading, setOtpLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  const [loginState, setLoginState] = useState({
    email: "",
    password: "",
    stayLogin: false,
  });
  const [showLoginPwd, setShowLoginPwd] = useState(false);

  const [regState, setRegState] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    mobile: "",
    dial_code: "+1",
  });
  const [showRegPwd, setShowRegPwd] = useState(false);
  const [showRegConfirm, setShowRegConfirm] = useState(false);

  const [forgotOpen, setForgotOpen] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetToken, setResetToken] = useState("");

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");
    if (token) {
      setResetToken(token);
      setResetOpen(true);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  function startResendCooldown() {
    setResendCooldown(60);
    const interval = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }

  async function handleLogin() {
    const res = await hitAxios({
      path: "/api/user/login",
      post: true,
      obj: loginState,
      showSnackbar: true,
    });
    if (res?.data?.success) {
      localStorage.setItem(
        process.env.REACT_APP_TOKEN + "_user",
        res.data.token,
      );
      history.push("/user");
    }
  }

  async function handleRegister() {
    if (regState.password !== regState.confirmPassword) return;
    const payload = {
      name: regState.name,
      email: regState.email,
      password: regState.password,
      mobile: regState.mobile,
      dial_code: regState.dial_code,
    };
    const res = await hitAxios({
      path: "/api/user/register",
      post: true,
      obj: payload,
      showSnackbar: false,
    });
    if (res?.data?.success) {
      setMode("login");
      setLoginState({ email: regState.email, password: "", stayLogin: false });
      setRegState({
        name: "",
        email: "",
        password: "",
        confirmPassword: "",
        mobile: "",
        dial_code: "+1",
      });
      return;
    }
    if (res?.data?.requiresVerification) {
      const otpRes = await hitAxios({
        path: "/api/user/send-register-otp",
        post: true,
        obj: payload,
        showSnackbar: true,
      });
      if (otpRes?.data?.success) {
        setPendingEmail(regState.email.trim().toLowerCase());
        setMode("verify");
        startResendCooldown();
      }
    }
  }

  async function handleVerifyOtp() {
    setOtpLoading(true);
    const res = await hitAxios({
      path: "/api/user/verify-register-otp",
      post: true,
      obj: { email: pendingEmail, otp: otpValue.trim() },
      showSnackbar: true,
    });
    setOtpLoading(false);
    if (res?.data?.success) {
      setMode("login");
      setLoginState({ email: pendingEmail, password: "", stayLogin: false });
      setOtpValue("");
      setPendingEmail("");
      setRegState({
        name: "",
        email: "",
        password: "",
        confirmPassword: "",
        mobile: "",
        dial_code: "+1",
      });
    }
    if (res?.data?.expired) {
      setMode("register");
      setOtpValue("");
      setPendingEmail("");
    }
  }

  async function handleResendOtp() {
    const res = await hitAxios({
      path: "/api/user/resend-register-otp",
      post: true,
      obj: { email: pendingEmail },
      showSnackbar: true,
    });
    if (res?.data?.success) startResendCooldown();
  }

  const appName = webData?.app_name || "App";

  // ── Shared input style ──
  const inputSx = {
    "& .MuiOutlinedInput-root": { borderRadius: 2 },
  };

  // ── Shared card style ──
  const cardSx = {
    p: { xs: 3, sm: 4 },
    borderRadius: 3,
    border: `1px solid ${theme.palette.divider}`,
    bgcolor: "background.paper",
    boxShadow: `0 16px 48px ${alpha(theme.palette.common.black, isDark ? 0.4 : 0.08)}`,
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "background.default",
        px: 2,
        py: 4,
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Background blobs */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          overflow: "hidden",
          pointerEvents: "none",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "-15%",
            right: "-5%",
            width: 500,
            height: 500,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, isDark ? 0.1 : 0.05)} 0%, transparent 70%)`,
            filter: "blur(60px)",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: "-15%",
            left: "-5%",
            width: 400,
            height: 400,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${alpha(theme.palette.secondary.main, isDark ? 0.08 : 0.04)} 0%, transparent 70%)`,
            filter: "blur(60px)",
          }}
        />
      </Box>

      {/* Theme toggle */}
      <Box sx={{ position: "fixed", top: 16, right: 16, zIndex: 10 }}>
        <ThemeToggle />
      </Box>

      <Container maxWidth="xs" sx={{ position: "relative", zIndex: 1 }}>
        {/* ── Brand mark ── */}
        <Stack alignItems="center" spacing={1} sx={{ mb: 3 }}>
          {/* ── Brand mark ── */}
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: "14px",
              // ✅ Fix: use contrasting colors based on mode
              background: isDark
                ? "#FFFFFF"
                : `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: isDark
                ? `0 8px 24px rgba(255,255,255,0.15)`
                : `0 8px 24px ${alpha(theme.palette.primary.main, 0.35)}`,
              mb: 0.5,
            }}
          >
            <AutoAwesomeIcon
              sx={{
                fontSize: 24,
                // ✅ Fix: flip icon color per mode
                color: isDark ? "#000000" : "#fff",
              }}
            />
          </Box>

          <Typography
            variant="h5"
            fontWeight={800}
            sx={{ color: "text.primary", letterSpacing: "-0.03em" }}
          >
            {mode === "login"
              ? lang?.welcomeBack || "Welcome back"
              : mode === "verify"
                ? lang?.verifyYourEmail || "Verify your email"
                : lang?.createAccount || "Create an account"}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            fontSize="0.82rem"
            textAlign="center"
          >
            {mode === "login"
              ? lang?.loginSubtitle || `Sign in to ${appName}`
              : mode === "verify"
                ? lang?.verifySubtitle || "Enter the code we sent you"
                : lang?.registerSubtitle ||
                  "Fill in the details below to get started"}
          </Typography>
        </Stack>

        {/* ══════════ LOGIN ══════════ */}
        <Fade in={mode === "login"} unmountOnExit>
          <Box>
            {mode === "login" && (
              <Paper elevation={0} sx={cardSx}>
                <Stack spacing={2}>
                  <TextField
                    value={loginState.email}
                    onChange={(e) =>
                      setLoginState({ ...loginState, email: e.target.value })
                    }
                    fullWidth
                    label={lang?.email || "Email"}
                    placeholder="you@example.com"
                    type="email"
                    sx={inputSx}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailOutlined
                            sx={{ color: "text.disabled", fontSize: 18 }}
                          />
                        </InputAdornment>
                      ),
                    }}
                  />

                  <TextField
                    fullWidth
                    value={loginState.password}
                    onChange={(e) =>
                      setLoginState({ ...loginState, password: e.target.value })
                    }
                    label={lang?.password || "Password"}
                    placeholder="••••••••"
                    type={showLoginPwd ? "text" : "password"}
                    onKeyDown={(e) => e.key === "Enter" && handleLogin()}
                    sx={inputSx}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockOutlined
                            sx={{ color: "text.disabled", fontSize: 18 }}
                          />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowLoginPwd(!showLoginPwd)}
                            edge="end"
                            size="small"
                            sx={{ color: "text.disabled" }}
                          >
                            {showLoginPwd ? (
                              <VisibilityOff fontSize="small" />
                            ) : (
                              <Visibility fontSize="small" />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  {/* Stay logged in + Forgot */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mt: -0.5,
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={loginState.stayLogin}
                          onChange={(e) =>
                            setLoginState({
                              ...loginState,
                              stayLogin: e.target.checked,
                            })
                          }
                          size="small"
                          sx={{ p: 0.5 }}
                        />
                      }
                      label={
                        <Typography
                          variant="caption"
                          sx={{
                            color: "text.secondary",
                            fontSize: "0.8rem",
                            userSelect: "none",
                          }}
                        >
                          {lang?.stayLoggedIn || "Stay logged in"}
                        </Typography>
                      }
                      sx={{ m: 0, gap: 0.5 }}
                    />
                    <Typography
                      variant="caption"
                      onClick={() => setForgotOpen(true)}
                      sx={{
                        color: "primary.main",
                        cursor: "pointer",
                        fontWeight: 600,
                        fontSize: "0.8rem",
                        flexShrink: 0,
                        "&:hover": { textDecoration: "underline" },
                      }}
                    >
                      {lang?.forgotPasswordLink || "Forgot password?"}
                    </Typography>
                  </Box>

                  <Button
                    onClick={handleLogin}
                    fullWidth
                    variant="contained"
                    size="large"
                    disabled={!loginState.email.trim() || !loginState.password}
                    sx={{ py: 1.4, mt: 0.5 }}
                  >
                    {lang?.signIn || "Continue"}
                  </Button>
                </Stack>

                <Divider sx={{ my: 2.5 }}>
                  <Typography
                    variant="caption"
                    color="text.disabled"
                    sx={{ px: 1, fontSize: "0.72rem" }}
                  >
                    OR
                  </Typography>
                </Divider>

                <Box textAlign="center">
                  <Typography
                    variant="body2"
                    sx={{ color: "text.secondary", fontSize: "0.82rem" }}
                  >
                    {lang?.noAccount || "Don't have an account?"}{" "}
                    <Typography
                      component="span"
                      variant="body2"
                      onClick={() => setMode("register")}
                      sx={{
                        color: "primary.main",
                        fontWeight: 700,
                        cursor: "pointer",
                        fontSize: "0.82rem",
                        "&:hover": { textDecoration: "underline" },
                      }}
                    >
                      {lang?.signUp || "Sign up"}
                    </Typography>
                  </Typography>
                </Box>
              </Paper>
            )}
          </Box>
        </Fade>

        {/* ══════════ REGISTER ══════════ */}
        <Fade in={mode === "register"} unmountOnExit>
          <Box>
            {mode === "register" && (
              <Paper elevation={0} sx={cardSx}>
                <Stack spacing={2}>
                  <TextField
                    value={regState.name}
                    onChange={(e) =>
                      setRegState({ ...regState, name: e.target.value })
                    }
                    fullWidth
                    label={lang?.fullName || "Full Name"}
                    placeholder="Your full name"
                    sx={inputSx}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonOutlined
                            sx={{ color: "text.disabled", fontSize: 18 }}
                          />
                        </InputAdornment>
                      ),
                    }}
                  />

                  <TextField
                    value={regState.email}
                    onChange={(e) =>
                      setRegState({ ...regState, email: e.target.value })
                    }
                    fullWidth
                    type="email"
                    label={lang?.email || "Email"}
                    placeholder="you@example.com"
                    sx={inputSx}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailOutlined
                            sx={{ color: "text.disabled", fontSize: 18 }}
                          />
                        </InputAdornment>
                      ),
                    }}
                  />

                  <TextField
                    value={regState.mobile}
                    onChange={(e) =>
                      setRegState({ ...regState, mobile: e.target.value })
                    }
                    fullWidth
                    type="tel"
                    label={lang?.mobile || "Mobile (optional)"}
                    placeholder="Phone number"
                    sx={inputSx}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start" sx={{ mr: 0.5 }}>
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={0.5}
                          >
                            <PhoneOutlined
                              sx={{ color: "text.disabled", fontSize: 18 }}
                            />
                            <CountryCodePicker
                              value={regState.dial_code}
                              onChange={(dc) =>
                                setRegState({ ...regState, dial_code: dc })
                              }
                            />
                          </Stack>
                        </InputAdornment>
                      ),
                    }}
                  />

                  <TextField
                    fullWidth
                    value={regState.password}
                    onChange={(e) =>
                      setRegState({ ...regState, password: e.target.value })
                    }
                    label={lang?.password || "Password"}
                    placeholder="••••••••"
                    type={showRegPwd ? "text" : "password"}
                    sx={inputSx}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockOutlined
                            sx={{ color: "text.disabled", fontSize: 18 }}
                          />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowRegPwd(!showRegPwd)}
                            edge="end"
                            size="small"
                            sx={{ color: "text.disabled" }}
                          >
                            {showRegPwd ? (
                              <VisibilityOff fontSize="small" />
                            ) : (
                              <Visibility fontSize="small" />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  <TextField
                    fullWidth
                    value={regState.confirmPassword}
                    onChange={(e) =>
                      setRegState({
                        ...regState,
                        confirmPassword: e.target.value,
                      })
                    }
                    label={lang?.confirmPassword || "Confirm Password"}
                    placeholder="••••••••"
                    type={showRegConfirm ? "text" : "password"}
                    error={
                      regState.confirmPassword.length > 0 &&
                      regState.password !== regState.confirmPassword
                    }
                    helperText={
                      regState.confirmPassword.length > 0 &&
                      regState.password !== regState.confirmPassword
                        ? lang?.passwordMismatch || "Passwords do not match"
                        : ""
                    }
                    onKeyDown={(e) => e.key === "Enter" && handleRegister()}
                    sx={inputSx}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockOutlined
                            sx={{ color: "text.disabled", fontSize: 18 }}
                          />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowRegConfirm(!showRegConfirm)}
                            edge="end"
                            size="small"
                            sx={{ color: "text.disabled" }}
                          >
                            {showRegConfirm ? (
                              <VisibilityOff fontSize="small" />
                            ) : (
                              <Visibility fontSize="small" />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  <Button
                    onClick={handleRegister}
                    fullWidth
                    variant="contained"
                    size="large"
                    disabled={
                      !regState.name.trim() ||
                      !regState.email.trim() ||
                      !regState.password ||
                      regState.password !== regState.confirmPassword
                    }
                    sx={{ py: 1.4, mt: 0.5 }}
                  >
                    {lang?.createAccount || "Create Account"}
                  </Button>
                </Stack>

                <Divider sx={{ my: 2.5 }}>
                  <Typography
                    variant="caption"
                    color="text.disabled"
                    sx={{ px: 1, fontSize: "0.72rem" }}
                  >
                    OR
                  </Typography>
                </Divider>

                <Box textAlign="center">
                  <Typography
                    variant="body2"
                    sx={{ color: "text.secondary", fontSize: "0.82rem" }}
                  >
                    {lang?.alreadyHaveAccount || "Already have an account?"}{" "}
                    <Typography
                      component="span"
                      variant="body2"
                      onClick={() => setMode("login")}
                      sx={{
                        color: "primary.main",
                        fontWeight: 700,
                        cursor: "pointer",
                        fontSize: "0.82rem",
                        "&:hover": { textDecoration: "underline" },
                      }}
                    >
                      {lang?.signIn || "Sign in"}
                    </Typography>
                  </Typography>
                </Box>
              </Paper>
            )}
          </Box>
        </Fade>

        {/* ══════════ VERIFY OTP ══════════ */}
        <Fade in={mode === "verify"} unmountOnExit>
          <Box>
            {mode === "verify" && (
              <Paper elevation={0} sx={cardSx}>
                <Stack spacing={3} alignItems="center">
                  <Box
                    sx={{
                      width: 72,
                      height: 72,
                      borderRadius: "50%",
                      bgcolor: alpha(
                        theme.palette.primary.main,
                        isDark ? 0.15 : 0.1,
                      ),
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.2)}`,
                    }}
                  >
                    <MarkEmailReadIcon
                      sx={{ fontSize: 34, color: "primary.main" }}
                    />
                  </Box>

                  <Box textAlign="center">
                    <Typography
                      variant="body1"
                      fontWeight={700}
                      gutterBottom
                      color="text.primary"
                    >
                      {lang?.checkYourEmail || "Check your email"}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      fontSize="0.82rem"
                    >
                      {lang?.otpSentTo || "We sent a 6-digit code to"}{" "}
                      <Typography
                        component="span"
                        variant="body2"
                        fontWeight={700}
                        color="primary.main"
                        fontSize="0.82rem"
                      >
                        {pendingEmail}
                      </Typography>
                    </Typography>
                  </Box>

                  <TextField
                    fullWidth
                    value={otpValue}
                    onChange={(e) =>
                      setOtpValue(e.target.value.replace(/\D/g, "").slice(0, 6))
                    }
                    label={lang?.verificationCode || "Verification Code"}
                    placeholder="000000"
                    inputProps={{
                      maxLength: 6,
                      style: {
                        textAlign: "center",
                        fontSize: "1.8rem",
                        letterSpacing: "0.7rem",
                        fontWeight: 800,
                        paddingLeft: "1.2rem",
                      },
                    }}
                    onKeyDown={(e) =>
                      e.key === "Enter" &&
                      otpValue.length === 6 &&
                      handleVerifyOtp()
                    }
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        "&.Mui-focused fieldset": {
                          borderColor: "primary.main",
                          borderWidth: "2px",
                        },
                      },
                    }}
                  />

                  <Button
                    onClick={handleVerifyOtp}
                    fullWidth
                    variant="contained"
                    size="large"
                    disabled={otpValue.length !== 6 || otpLoading}
                    sx={{ py: 1.4 }}
                  >
                    {otpLoading
                      ? lang?.verifying || "Verifying..."
                      : lang?.verifyEmail || "Verify Email"}
                  </Button>

                  <Stack direction="row" alignItems="center" spacing={0.75}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontSize="0.8rem"
                    >
                      {lang?.didntReceiveCode || "Didn't receive the code?"}
                    </Typography>
                    {resendCooldown > 0 ? (
                      <Typography
                        variant="caption"
                        color="text.disabled"
                        fontSize="0.8rem"
                      >
                        {lang?.resendIn || "Resend in"} {resendCooldown}s
                      </Typography>
                    ) : (
                      <Typography
                        variant="caption"
                        onClick={handleResendOtp}
                        sx={{
                          color: "primary.main",
                          fontWeight: 700,
                          cursor: "pointer",
                          fontSize: "0.8rem",
                          "&:hover": { textDecoration: "underline" },
                        }}
                      >
                        {lang?.resendCode || "Resend code"}
                      </Typography>
                    )}
                  </Stack>

                  <Typography
                    variant="caption"
                    onClick={() => {
                      setMode("register");
                      setOtpValue("");
                    }}
                    sx={{
                      color: "text.disabled",
                      cursor: "pointer",
                      fontSize: "0.78rem",
                      "&:hover": { color: "text.secondary" },
                    }}
                  >
                    ← {lang?.backToRegister || "Back to register"}
                  </Typography>
                </Stack>
              </Paper>
            )}
          </Box>
        </Fade>

        {/* ── Footer ── */}
        <Stack
          direction="row"
          justifyContent="center"
          spacing={2.5}
          sx={{ mt: 3 }}
        >
          {[
            lang?.termsOfUse || "Terms of use",
            lang?.privacyPolicy || "Privacy policy",
            lang?.help || "Help",
          ].map((item) => (
            <Typography
              key={item}
              variant="caption"
              sx={{
                color: "text.disabled",
                fontSize: "0.72rem",
                cursor: "pointer",
                "&:hover": { color: "text.secondary" },
              }}
            >
              {item}
            </Typography>
          ))}
        </Stack>

        <Typography
          variant="caption"
          color="text.disabled"
          sx={{
            display: "block",
            textAlign: "center",
            mt: 1.5,
            fontSize: "0.7rem",
          }}
        >
          {lang?.copyrightText ||
            `© ${new Date().getFullYear()} ${appName}. All rights reserved.`}
        </Typography>
      </Container>

      <ForgotPasswordDialog
        open={forgotOpen}
        onClose={() => setForgotOpen(false)}
        lang={lang}
        hitAxios={hitAxios}
      />
      <ResetPasswordDialog
        open={resetOpen}
        onClose={() => setResetOpen(false)}
        token={resetToken}
        lang={lang}
        hitAxios={hitAxios}
        onSuccess={() =>
          setLoginState({ email: "", password: "", stayLogin: false })
        }
      />
    </Box>
  );
};

export default UserLogin;
